const { generateSeed } = require('./math/seed-rng');
const { getRandomizer, randomizers } = require('./randomizers');
const { resultToCsv, resultToOperationalCsv } = require('./export/csv');
const { resultToJson } = require('./export/json');
const { resultToRScript } = require('./export/r-script');
const { resultToPythonScript } = require('./export/python');
const { manifestToJson } = require('./export/integrity-manifest');
const { resultToMethodsText } = require('./export/methods');
const { buildReportHtml, createExperimentSignature } = require('./export/report');
const { AuditTrail, auditTrailToText } = require('./audit/trail');
const {
  buildOperationalExport,
  closeOperationalSession,
  createOperationalSession,
  createSeedCommit,
  getOperationalRows,
  MODE_OPERATIONAL,
  MODE_PLANNING,
  revealNextAllocation
} = require('./security/operational');
const { buildExportIntegrityManifest, hashArtifact, sha256Sidecar } = require('./security/export-integrity');
const { verifyRandomization } = require('./security/verification');
const {
  getCsvTargetField,
  inferStratificationColumns,
  parseCsv,
  rowsToTechniqueInput,
  templateToCsv
} = require('./ui/csv-tools');
const { getMethodGuidance } = require('./ui/method-guidance');
const { getTechniqueParameterConfig, normalizeTechniqueParams } = require('./ui/technique-params');
const { getTranslations, normalizeLanguage } = require('./i18n/translations');
const {
  clearDraft,
  clearLastResult,
  clearOfflineQueue,
  enqueueOfflineEvent,
  loadDraft,
  loadLanguage,
  loadLastResult,
  loadOfflineQueue,
  saveDraft,
  saveLanguage,
  saveLastResult
} = require('./storage/local-state');
const {
  APP_VERSION,
  MAX_PARTICIPANTS,
  SEQUENCE_PREVIEW_LIMIT
} = require('./constants');

const appState = {
  currentStep: 1,
  result: null,
  signature: null,
  csvDraft: null,
  auditTrail: new AuditTrail(),
  language: 'pt',
  operationMode: MODE_PLANNING,
  operationalSession: null,
  seedCommit: null,
  isRestoring: false,
  saveDraftTimer: null
};

function byId(id) {
  return document.getElementById(id);
}

function t() {
  return getTranslations(appState.language);
}

function getSelectedOperationMode() {
  const checked = document.querySelector('input[name="operationMode"]:checked');
  return checked && checked.value === MODE_OPERATIONAL ? MODE_OPERATIONAL : MODE_PLANNING;
}

function readForm() {
  const technique = document.querySelector('input[name="technique"]:checked').value;
  const rawParams = {
    studyName: byId('studyName').value.trim(),
    n: Number(byId('nPatients').value),
    arms: Number(byId('nArms').value),
    armLabels: Array.from(document.querySelectorAll('[data-arm-label-input]')).map((input) => input.value.trim()),
    seed: byId('seed').value.trim(),
    technique,
    blockSize: Number(byId('blockSize').value),
    blockSizes: byId('blockSizes').value,
    clusterSizes: byId('clusterSizes').value,
    strataSizes: byId('strataSizes').value,
    clusterStrata: byId('clusterStrata').value,
    clusterStrataSizes: byId('clusterStrataSizes').value
  };

  return normalizeTechniqueParams(rawParams);
}

function getArmLabel(result, index) {
  return result.parameters.armLabels[index] || `Arm ${index + 1}`;
}

function setStep(nextStep) {
  appState.currentStep = Math.min(4, Math.max(1, nextStep));

  document.querySelectorAll('[data-step-panel]').forEach((panel) => {
    panel.hidden = Number(panel.dataset.stepPanel) !== appState.currentStep;
  });

  document.querySelectorAll('[data-step]').forEach((item) => {
    item.classList.toggle('is-active', Number(item.dataset.step) === appState.currentStep);
    item.classList.toggle('is-complete', Number(item.dataset.step) < appState.currentStep);
  });
  scheduleDraftSave();
}

function focusCurrentStepTitle() {
  const title = document.querySelector(`[data-step-panel="${appState.currentStep}"] h2`);

  if (title) {
    title.focus({ preventScroll: false });
  }
}

function showStatus(message, type = 'info') {
  const status = byId('statusMessage');
  status.textContent = message;
  status.dataset.type = type;
  status.hidden = !message;
}

async function recordAudit(action, details = {}) {
  try {
    const event = await appState.auditTrail.record(action, {
      language: appState.language,
      ...details
    });
    enqueueOfflineEvent(event);
    renderAuditStatus();
    return event;
  } catch (error) {
    console.warn('randFlow audit event could not be recorded.', error);
    return null;
  }
}

function renderAuditStatus() {
  const eventCount = byId('auditEventCount');
  const chainHead = byId('auditChainHead');

  if (!eventCount || !chainHead) {
    return;
  }

  const snapshot = appState.auditTrail.snapshot();
  eventCount.textContent = String(snapshot.eventCount);
  chainHead.textContent = snapshot.chainHead;
  renderLocalDataStatus();
}

function renderLocalDataStatus() {
  const queueCount = byId('offlineQueueCount');

  if (!queueCount) {
    return;
  }

  const dictionary = t();
  const draft = loadDraft();
  const lastResult = loadLastResult();
  const queue = loadOfflineQueue();
  byId('draftSavedState').textContent = draft ? dictionary.localData.saved : dictionary.localData.empty;
  byId('lastResultState').textContent = lastResult ? dictionary.localData.saved : dictionary.localData.empty;
  queueCount.textContent = `${queue.length} ${dictionary.localData.events}`;
}

function getStoredTheme() {
  try {
    return localStorage.getItem('randflow-theme');
  } catch (error) {
    return null;
  }
}

function storeTheme(theme) {
  try {
    localStorage.setItem('randflow-theme', theme);
  } catch (error) {
    // Theme persistence is progressive enhancement only.
  }
}

function applyTheme(theme, shouldRecord = false) {
  const normalizedTheme = theme === 'dark' ? 'dark' : 'light';
  document.documentElement.dataset.theme = normalizedTheme;

  const toggle = byId('themeToggle');
  const label = byId('themeToggleLabel');

  if (toggle && label) {
    const isDark = normalizedTheme === 'dark';
    toggle.setAttribute('aria-pressed', String(isDark));
    label.textContent = isDark ? t().lightMode : t().darkMode;
  }

  storeTheme(normalizedTheme);

  if (shouldRecord) {
    recordAudit('theme_changed', { theme: normalizedTheme });
  }
}

function initializeTheme() {
  const storedTheme = getStoredTheme();
  const systemPrefersDark = window.matchMedia
    && window.matchMedia('(prefers-color-scheme: dark)').matches;
  applyTheme(storedTheme || (systemPrefersDark ? 'dark' : 'light'));
}

function setText(selector, value) {
  const element = document.querySelector(selector);

  if (element) {
    element.textContent = value;
  }
}

function setAllText(selector, values) {
  document.querySelectorAll(selector).forEach((element, index) => {
    if (values[index] !== undefined) {
      element.textContent = values[index];
    }
  });
}

function setLabelText(label, value) {
  if (label && label.childNodes[0]) {
    label.childNodes[0].textContent = value;
  }
}

function renderTechniqueCards() {
  const dictionary = t();

  Object.entries(dictionary.techniques).forEach(([technique, values]) => {
    const input = document.querySelector(`input[name="technique"][value="${technique}"]`);
    const card = input && input.closest('.technique-card');

    if (!card) {
      return;
    }

    const [title, description, randomizes, balances] = values;
    card.querySelector('strong').textContent = title;
    card.querySelector('small').textContent = description;
    input.setAttribute('aria-label', `${title}. ${description}. ${t().tags.randomizes}: ${randomizes}. ${t().tags.balances}: ${balances}.`);
    const chips = card.querySelectorAll('em');
    chips[0].innerHTML = `<b>${escapeHtml(dictionary.tags.randomizes)}</b> ${escapeHtml(randomizes)}`;
    chips[1].innerHTML = `<b>${escapeHtml(dictionary.tags.balances)}</b> ${escapeHtml(balances)}`;
  });
}

function getLocalizedMethodGuide(technique) {
  const base = getMethodGuidance(technique);
  const values = t().techniques[technique];

  if (!values) {
    return base;
  }

  return {
    family: values[0],
    randomizationUnit: values[2],
    balanceUnit: values[3],
    whenUse: values[1],
    avoid: t().preview.caution
  };
}

function getLocalizedParameterMessage(technique, fallback) {
  const messages = {
    pt: {
      simple: 'A randomização simples precisa apenas do seed; blocos e clusters não são usados.',
      blocked: 'Blocos fixos precisam de tamanho divisível pelo número de braços, e N deve ser divisível pelo bloco.',
      'random-block': 'Blocos aleatórios precisam de uma lista de tamanhos permitidos separados por vírgula.',
      clustered: 'Cluster precisa de tamanhos separados por vírgula; clusters nomeados podem usar Nome:tamanho.',
      'stratified-blocked': 'Estratificação por blocos fixos precisa de estratos Nome:tamanho e bloco que divida todos os estratos.',
      'stratified-random-block': 'Estratificação por blocos aleatórios precisa de estratos Nome:tamanho e tamanhos de bloco permitidos.',
      'cluster-stratum': 'Cluster como estrato randomiza participantes dentro de cada centro; clusters balanceiam, mas não definem tratamento único.',
      'cluster-stratified': 'Cluster estratificado atribui clusters inteiros dentro de cada estrato; use Estrato|Cluster:tamanho.'
    },
    en: {
      simple: fallback,
      blocked: fallback,
      'random-block': fallback,
      clustered: fallback,
      'stratified-blocked': fallback,
      'stratified-random-block': fallback,
      'cluster-stratum': fallback,
      'cluster-stratified': fallback
    },
    es: {
      simple: 'La aleatorización simple solo necesita la seed; no usa bloques ni clusters.',
      blocked: 'Bloques fijos necesitan tamaño divisible por el número de brazos, y N debe ser divisible por el bloque.',
      'random-block': 'Bloques aleatorios necesitan una lista de tamaños permitidos separados por coma.',
      clustered: 'Cluster necesita tamaños separados por coma; clusters con nombre pueden usar Nombre:tamaño.',
      'stratified-blocked': 'Estratificación por bloques fijos necesita estratos Nombre:tamaño y un bloque que divida todos los estratos.',
      'stratified-random-block': 'Estratificación por bloques aleatorios necesita estratos Nombre:tamaño y tamaños de bloque permitidos.',
      'cluster-stratum': 'Cluster como estrato aleatoriza participantes dentro de cada centro; los clusters balancean, pero no definen un tratamiento único.',
      'cluster-stratified': 'Cluster estratificado asigna clusters completos dentro de cada estrato; use Estrato|Cluster:tamaño.'
    }
  };

  return (messages[appState.language] && messages[appState.language][technique]) || fallback;
}

function renderStaticText() {
  const dictionary = t();
  document.documentElement.lang = dictionary.locale;
  document.title = `randFlow - ${dictionary.appTitle}`;

  setText('h1', dictionary.appTitle);
  setText('#validationReportLink', dictionary.validationReportLink);
  const validationReportLink = byId('validationReportLink');
  if (validationReportLink) {
    validationReportLink.href = `docs/validation/RANDFLOW_VALIDATION_REPORT_V2.html?lang=${encodeURIComponent(appState.language)}`;
  }
  setText('#userManualLink', dictionary.userManualLink);
  const userManualLink = byId('userManualLink');
  if (userManualLink) {
    userManualLink.href = `docs/manual/RANDFLOW_USER_MANUAL_V2.html?lang=${encodeURIComponent(appState.language)}`;
  }
  document.querySelectorAll('[data-step]').forEach((step, index) => {
    step.innerHTML = `<strong>${index + 1}</strong> ${escapeHtml(dictionary.steps[index])}`;
  });

  const panels = document.querySelectorAll('[data-step-panel]');
  panels[0].querySelector('h2').textContent = dictionary.study.title;
  panels[0].querySelector('.panel-heading p').textContent = dictionary.study.description;
  document.querySelectorAll('[data-step-panel="1"] .form-grid > label').forEach((label, index) => {
    setLabelText(label, [
      dictionary.study.name,
      dictionary.study.participants,
      dictionary.study.arms
    ][index]);
  });
  byId('studyName').placeholder = dictionary.study.namePlaceholder;
  Array.from(byId('nArms').options).forEach((option, index) => {
    option.textContent = dictionary.study.armsOptions[index];
  });
  setText('.arm-labels-panel h3', dictionary.study.armNames);
  setText('.arm-labels-panel p', dictionary.study.armDescription);
  setText('#securityModeEyebrow', dictionary.operational.securityEyebrow);
  setText('#securityModeTitle', dictionary.operational.modeTitle);
  setText('#securityModeDescription', dictionary.operational.modeDescription);
  setText('#planningModeLabel', dictionary.operational.planningLabel);
  setText('#planningModeHelp', dictionary.operational.planningHelp);
  setText('#operationalModeLabel', dictionary.operational.operationalLabel);
  setText('#operationalModeHelp', dictionary.operational.operationalHelp);
  byId('resetButton').textContent = dictionary.study.clear;

  panels[1].querySelector('h2').textContent = dictionary.technique.title;
  panels[1].querySelector('.panel-heading p').textContent = dictionary.technique.description;
  setText('.method-assistant .microcopy', dictionary.technique.assisted);
  document.querySelector('[data-method-filter="all"]').textContent = dictionary.technique.filters.all;
  document.querySelector('[data-method-filter="participant"]').textContent = dictionary.technique.filters.participant;
  document.querySelector('[data-method-filter="stratified"]').textContent = dictionary.technique.filters.stratified;
  document.querySelector('[data-method-filter="cluster"]').textContent = dictionary.technique.filters.cluster;

  const familyHeadings = document.querySelectorAll('.family-heading');
  [
    dictionary.technique.families.basic,
    dictionary.technique.families.blocks,
    dictionary.technique.families.stratified,
    dictionary.technique.families.cluster
  ].forEach((family, index) => {
    familyHeadings[index].querySelector('span').textContent = family[0];
    familyHeadings[index].querySelector('small').textContent = family[1];
  });
  renderTechniqueCards();

  panels[2].querySelector('h2').textContent = dictionary.parameters.title;
  panels[2].querySelector('.panel-heading p').textContent = dictionary.parameters.description;
  document.querySelectorAll('[data-step-panel="3"] .form-grid-params > label').forEach((label, index) => {
    setLabelText(label, [
      dictionary.parameters.seed,
      dictionary.parameters.blockSize,
      dictionary.parameters.blockSizes,
      dictionary.parameters.clusterSizes,
      dictionary.parameters.strata,
      dictionary.parameters.clusterStrata,
      dictionary.parameters.clusterStrataSizes
    ][index]);
  });
  byId('newSeedButton').setAttribute('aria-label', dictionary.parameters.generateSeed);
  byId('newSeedButton').title = dictionary.parameters.generateSeed;
  byId('copySeedButton').setAttribute('aria-label', dictionary.parameters.copySeed);
  byId('copySeedButton').title = dictionary.parameters.copySeed;
  setText('#csvTools h3', dictionary.parameters.csvTitle);
  setText('#csvTools > div > p', dictionary.parameters.csvDescription);
  document.querySelector('#participantStrataTools label').childNodes[0].textContent = dictionary.parameters.strataColumns;
  setText('#participantStrataTools small', dictionary.parameters.strataColumnsHelp);
  byId('downloadTemplateButton').textContent = dictionary.parameters.downloadTemplate;
  document.querySelector('.file-button').childNodes[0].textContent = dictionary.parameters.importCsv;
  setText('#csvPreview .microcopy', dictionary.parameters.csvPreview);
  setText('#csvPreview h3', dictionary.parameters.csvCheck);
  byId('clearCsvPreviewButton').textContent = dictionary.parameters.discard;
  byId('applyCsvButton').textContent = dictionary.parameters.applyCsv;
  byId('generateButton').textContent = dictionary.parameters.generate;

  panels[3].querySelector('h2').textContent = dictionary.results.title;
  panels[3].querySelector('.panel-heading p').textContent = dictionary.results.description;
  setAllText('.result-metrics span', [
    dictionary.results.technique,
    'Seed',
    dictionary.results.imbalance,
    dictionary.results.validation
  ]);
  setAllText('.audit-strip span', [dictionary.results.auditEvents, dictionary.results.chainHead]);
  setText('#validationDecisionTitle', dictionary.validation.passTitle);
  setText('#validationDecisionBody', dictionary.validation.passBody);
  setText('.audit-strip p', dictionary.results.auditNote);
  setText('#localDataEyebrow', dictionary.localData.eyebrow);
  setText('#localDataTitle', dictionary.localData.title);
  setText('#localDataSummary', dictionary.localData.summary);
  setText('#draftSavedLabel', dictionary.localData.draft);
  setText('#lastResultLabel', dictionary.localData.lastResult);
  setText('#offlineQueueLabel', dictionary.localData.queue);
  byId('exportQueueButton').textContent = dictionary.localData.exportQueue;
  byId('clearQueueButton').textContent = dictionary.localData.clearQueue;
  byId('clearLocalDataButton').textContent = dictionary.localData.clearLocalData;
  setText('#operationalEyebrow', dictionary.operational.eyebrow);
  setText('#operationalTitle', dictionary.operational.title);
  setText('#operationalDescription', dictionary.operational.description);
  setText('#revealedLabel', dictionary.operational.revealed);
  setText('#concealedLabel', dictionary.operational.concealed);
  setText('#seedCommitLabel', dictionary.operational.seedCommit);
  byId('revealNextButton').textContent = dictionary.operational.revealNext;
  byId('copySeedCommitButton').textContent = dictionary.operational.copySeedCommit;
  byId('exportOperationalButton').textContent = dictionary.operational.exportOperational;
  byId('exportIntegrityBundleButton').textContent = dictionary.operational.exportIntegrity;
  setText('#verificationEyebrow', dictionary.operational.verificationEyebrow);
  setText('#verificationTitle', dictionary.operational.verificationTitle);
  setText('#verificationDescription', dictionary.operational.verificationDescription);
  byId('fillVerificationButton').textContent = dictionary.operational.fillVerification;
  byId('runVerificationButton').textContent = dictionary.operational.runVerification;
  renderLocalDataStatus();
  setText('.split-layout section:first-child h3', dictionary.results.armDistribution);
  setText('.split-layout section:nth-child(2) h3', dictionary.results.visibleSolution);
  setText('.warning-list', appState.result && appState.result.metrics.warnings.length ? '' : dictionary.results.noWarnings);
  const resultHeaders = document.querySelectorAll('[data-step-panel="4"] th');
  [
    dictionary.results.arm,
    'N',
    '%',
    dictionary.results.patient,
    dictionary.results.order,
    dictionary.results.arm,
    dictionary.results.block,
    dictionary.results.cluster,
    dictionary.results.stratum
  ].forEach((value, index) => {
    if (resultHeaders[index]) {
      resultHeaders[index].textContent = value;
    }
  });
  setText('.split-layout section:first-child h3 + table + h3', dictionary.results.warnings);
  byId('exportMethodsButton').textContent = dictionary.results.methods;
  byId('exportAuditButton').textContent = dictionary.results.audit;
  byId('exportManifestButton').textContent = dictionary.results.manifest;
  byId('copySignatureButton').textContent = dictionary.results.signature;
  byId('reportButton').textContent = dictionary.results.report;

  document.querySelectorAll('[data-next-step]').forEach((button) => {
    button.textContent = dictionary.study.next;
  });
  document.querySelectorAll('[data-prev-step]').forEach((button) => {
    button.textContent = dictionary.study.back;
  });
  applyTheme(document.documentElement.dataset.theme || 'light');
}

function applyLanguage(language, shouldRecord = false) {
  appState.language = normalizeLanguage(language);
  byId('languageSelect').value = appState.language;
  saveLanguage(appState.language);
  renderStaticText();
  updateArmLabelInputs();
  updateTechniqueParameters();

  if (appState.result) {
    renderSummary(appState.result);
    renderSequence(appState.result);
  }

  if (shouldRecord) {
    recordAudit('language_changed', { language: appState.language });
    showStatus(t().status.languageChanged, 'success');
  }
}

function escapeHtml(value) {
  return String(value === undefined || value === null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function validateStudyDefinition() {
  const n = Number(byId('nPatients').value);
  const arms = Number(byId('nArms').value);

  if (!Number.isInteger(n) || n < 1 || n > MAX_PARTICIPANTS) {
    throw new Error(t().status.participantRange);
  }

  if (!Number.isInteger(arms) || arms < 2 || arms > 4) {
    throw new Error(t().status.armRange);
  }
}

function updateArmLabelInputs() {
  const arms = Number(byId('nArms').value);
  const container = byId('armLabels');
  const existingValues = Array.from(container.querySelectorAll('[data-arm-label-input]'))
    .map((input) => input.value);

  container.innerHTML = '';

  for (let index = 0; index < arms; index += 1) {
    const label = document.createElement('label');
    const input = document.createElement('input');

    input.type = 'text';
    input.maxLength = 60;
    input.value = existingValues[index] || `${t().study.armName} ${index + 1}`;
    input.dataset.armLabelInput = 'true';
    input.setAttribute('aria-label', `${t().study.armName} ${index + 1}`);

    label.textContent = `${t().study.armName} ${index + 1}`;
    label.appendChild(input);
    container.appendChild(label);
  }
}

function goToStep(nextStep) {
  try {
    if (nextStep > 1) {
      validateStudyDefinition();
    }

    setStep(nextStep);
    focusCurrentStepTitle();
    showStatus('', 'info');
  } catch (error) {
    showStatus(error.message, 'error');
  }
}

function updateTechniqueParameters() {
  const technique = document.querySelector('input[name="technique"]:checked').value;
  const config = getTechniqueParameterConfig(technique);

  document.querySelectorAll('[data-technique-param]').forEach((field) => {
    const inputNames = field.dataset.techniqueFields.split(',');
    const isRelevant = inputNames.some((inputName) => config.fields.includes(inputName));
    field.hidden = !isRelevant;
    field.setAttribute('aria-hidden', String(!isRelevant));
    field.querySelectorAll('input, select, textarea').forEach((input) => {
      input.disabled = !isRelevant;
      input.required = isRelevant;
      input.tabIndex = isRelevant ? 0 : -1;
    });
  });

  byId('techniqueParamHelper').textContent = getLocalizedParameterMessage(technique, config.message);

  if (technique === 'clustered') {
    syncSizedGroupDefault('clusterSizes', 4);
  }

  if (technique === 'stratified-blocked' || technique === 'stratified-random-block') {
    if (technique === 'stratified-blocked') {
      syncStratifiedFixedDefault();
    } else {
      syncSizedGroupDefault('strataSizes', 2);
    }
  }

  if (technique === 'cluster-stratum') {
    syncSizedGroupDefault('clusterStrata', 2, 'Center');
  }

  if (technique === 'cluster-stratified') {
    syncClusterStratifiedDefault();
  }

  const csvTargetField = getCsvTargetField(technique);
  const csvTools = byId('csvTools');
  const participantStrataTools = byId('participantStrataTools');
  const isParticipantStratified = technique === 'stratified-blocked' || technique === 'stratified-random-block';
  csvTools.hidden = !csvTargetField;
  csvTools.setAttribute('aria-hidden', String(!csvTargetField));
  participantStrataTools.hidden = !isParticipantStratified;

  if (!csvTargetField) {
    clearCsvDraft();
  } else if (appState.csvDraft && appState.csvDraft.technique !== technique) {
    clearCsvDraft();
  }

  renderMethodGuide(technique);
  renderMethodPreview(technique);
}

function renderMethodGuide(technique) {
  const guide = getLocalizedMethodGuide(technique);

  byId('methodGuide').innerHTML = `
    <div>
      <p class="microcopy">${escapeHtml(t().preview.selectedMethod)}</p>
      <h3>${escapeHtml(guide.family)}</h3>
    </div>
    <div class="method-guide-grid">
      <div>
        <span>${escapeHtml(t().preview.randomizes)}</span>
        <strong>${escapeHtml(guide.randomizationUnit)}</strong>
      </div>
      <div>
        <span>${escapeHtml(t().preview.balances)}</span>
        <strong>${escapeHtml(guide.balanceUnit)}</strong>
      </div>
      <div>
        <span>${escapeHtml(t().preview.whenUse)}</span>
        <p>${escapeHtml(guide.whenUse)}</p>
      </div>
      <div>
        <span>${escapeHtml(t().preview.caution)}</span>
        <p>${escapeHtml(guide.avoid)}</p>
      </div>
    </div>
  `;
}

function renderMethodPreview(technique) {
  const guide = getLocalizedMethodGuide(technique);
  const participantCount = byId('nPatients').value || '-';
  const armCount = byId('nArms').value || '-';
  const seed = byId('seed').value || t().preview.pendingSeed;

  byId('methodPreview').innerHTML = `
    <div>
      <p class="microcopy">${escapeHtml(t().preview.methodPreview)}</p>
      <h3>${escapeHtml(guide.family)}</h3>
    </div>
    <div class="preview-grid">
      <div><span>${escapeHtml(t().preview.participants)}</span><strong>${escapeHtml(participantCount)}</strong></div>
      <div><span>${escapeHtml(t().preview.arms)}</span><strong>${escapeHtml(armCount)}</strong></div>
      <div><span>${escapeHtml(t().preview.randomizedUnit)}</span><strong>${escapeHtml(guide.randomizationUnit)}</strong></div>
      <div><span>${escapeHtml(t().preview.balance)}</span><strong>${escapeHtml(guide.balanceUnit)}</strong></div>
    </div>
    <p class="preview-note">${escapeHtml(t().preview.currentSeed)}: <code>${escapeHtml(seed)}</code>. ${escapeHtml(guide.whenUse)}</p>
  `;
}

function applyMethodFilter(filter) {
  document.querySelectorAll('[data-method-filter]').forEach((button) => {
    button.classList.toggle('is-active', button.dataset.methodFilter === filter);
  });

  document.querySelectorAll('[data-method-family]').forEach((section) => {
    section.hidden = filter !== 'all' && section.dataset.methodFamily !== filter;
  });

  const selectedTechnique = document.querySelector('input[name="technique"]:checked');

  if (selectedTechnique && selectedTechnique.closest('[data-method-family]').hidden) {
    const firstVisibleTechnique = document.querySelector('[data-method-family]:not([hidden]) input[name="technique"]');

    if (firstVisibleTechnique) {
      firstVisibleTechnique.checked = true;
      updateTechniqueParameters();
    }
  }
}

function syncSizedGroupDefault(inputId, groupCount, labelPrefix = null) {
  if (appState.isRestoring) {
    return;
  }

  const n = Number(byId('nPatients').value);
  const input = byId(inputId);

  if (!Number.isInteger(n) || n < groupCount || document.activeElement === input) {
    return;
  }

  const base = Math.floor(n / groupCount);
  const sizes = Array.from({ length: groupCount }, (_, index) => {
    const size = index === groupCount - 1 ? n - (base * (groupCount - 1)) : base;
    return labelPrefix ? `${labelPrefix} ${index + 1}:${size}` : size;
  });
  input.value = sizes.join(',');
}

function syncStratifiedFixedDefault() {
  if (appState.isRestoring) {
    return;
  }

  const n = Number(byId('nPatients').value);
  const blockSize = Number(byId('blockSize').value);
  const input = byId('strataSizes');

  if (!Number.isInteger(n) || !Number.isInteger(blockSize) || document.activeElement === input) {
    return;
  }

  if (n >= blockSize * 2 && n % blockSize === 0) {
    const firstBlocks = Math.max(1, Math.floor((n / blockSize) / 2));
    const first = firstBlocks * blockSize;
    input.value = `Stratum 1:${first},Stratum 2:${n - first}`;
    return;
  }

  syncSizedGroupDefault('strataSizes', 2);
}

function syncClusterStratifiedDefault() {
  if (appState.isRestoring) {
    return;
  }

  const n = Number(byId('nPatients').value);
  const input = byId('clusterStrataSizes');

  if (!Number.isInteger(n) || n < 4 || document.activeElement === input) {
    return;
  }

  const base = Math.floor(n / 4);
  const sizes = [base, base, base, n - (base * 3)];
  input.value = [
    `Norte|Manaus:${sizes[0]}`,
    `Norte|Belem:${sizes[1]}`,
    `Sudeste|Sao Paulo:${sizes[2]}`,
    `Sudeste|Rio:${sizes[3]}`
  ].join(',');
}

function renderSummary(result) {
  const armRows = result.metrics.armCounts
    .map((count, index) => `
      <tr>
        <td>${escapeHtml(getArmLabel(result, index))}</td>
        <td>${count}</td>
        <td>${((count / result.parameters.n) * 100).toFixed(1)}%</td>
      </tr>
    `)
    .join('');

  byId('summaryTableBody').innerHTML = armRows;
  byId('resultTechnique').textContent = result.algorithm;
  byId('resultSeed').textContent = result.seed;
  byId('resultImbalance').textContent = `${result.metrics.imbalance} (${result.metrics.imbalancePercent}%)`;
  byId('resultValidation').textContent = result.metrics.validation;
  const isWarn = result.metrics.validation === 'WARN';
  byId('validationDecision').dataset.validation = isWarn ? 'WARN' : 'PASS';
  byId('validationDecisionTitle').textContent = isWarn
    ? t().validation.warnTitle
    : t().validation.passTitle;
  byId('validationDecisionBody').textContent = isWarn
    ? t().validation.warnBody
    : t().validation.passBody;

  const warningList = byId('warningList');
  warningList.innerHTML = result.metrics.warnings.length
    ? result.metrics.warnings.map((warning) => `<li>${escapeHtml(warning)}</li>`).join('')
    : `<li>${escapeHtml(t().results.noWarnings)}</li>`;
}

function isOperationalActive() {
  return appState.operationMode === MODE_OPERATIONAL && appState.operationalSession;
}

function renderSequence(result) {
  const revealedCount = isOperationalActive() ? appState.operationalSession.revealedCount : result.sequence.length;
  const sourceRows = isOperationalActive()
    ? getOperationalRows(result, revealedCount, Math.min(result.sequence.length, SEQUENCE_PREVIEW_LIMIT))
    : result.sequence.slice(0, SEQUENCE_PREVIEW_LIMIT);
  const previewNote = byId('sequencePreviewNote');

  byId('sequenceTableBody').innerHTML = sourceRows
    .map((patient) => `
      <tr>
        <td>${escapeHtml(patient.patientId)}</td>
        <td>${patient.sequenceOrder}</td>
        <td class="${patient.revealStatus === 'concealed' ? 'concealed-cell' : ''}">${escapeHtml(patient.arm || t().operational.concealedCell)}</td>
        <td>${escapeHtml(patient.blockNumber || '-')}</td>
        <td>${escapeHtml(patient.cluster || '-')}</td>
        <td>${escapeHtml(patient.stratum || '-')}</td>
      </tr>
    `)
    .join('');

  if (result.sequence.length > SEQUENCE_PREVIEW_LIMIT) {
    previewNote.hidden = false;
    previewNote.textContent = t().results.sequencePreviewNote
      .replace('{shown}', String(SEQUENCE_PREVIEW_LIMIT))
      .replace('{total}', String(result.sequence.length));
  } else {
    previewNote.hidden = true;
    previewNote.textContent = '';
  }

  renderOperationalPanel();
}

function renderOperationalPanel() {
  const panel = byId('operationalPanel');

  if (!panel) {
    return;
  }

  const active = isOperationalActive();
  panel.hidden = !active;

  if (!active) {
    return;
  }

  const revealed = appState.operationalSession.revealedCount;
  const total = appState.operationalSession.total;
  byId('revealedCount').textContent = `${revealed}/${total}`;
  byId('concealedCount').textContent = String(Math.max(0, total - revealed));
  byId('seedCommitShort').textContent = appState.seedCommit ? appState.seedCommit.hash.slice(0, 16) : '-';
  byId('seedCommitInstructions').textContent = appState.seedCommit ? `${t().operational.commitInstructions} SHA-256: ${appState.seedCommit.hash}` : '';
  byId('revealNextButton').disabled = revealed >= total;
}

async function renderResult(result, randomizer) {
  appState.result = result;
  appState.signature = await createExperimentSignature(result);
  appState.operationMode = getSelectedOperationMode();
  appState.operationalSession = appState.operationMode === MODE_OPERATIONAL
    ? createOperationalSession(result)
    : null;
  appState.seedCommit = await createSeedCommit(result);

  await recordAudit('randomization_generated', {
    algorithmId: result.algorithmId,
    seed: result.seed,
    n: result.parameters.n,
    arms: result.parameters.arms,
    signature: appState.signature,
    operationMode: appState.operationMode
  });

  if (appState.operationMode === MODE_OPERATIONAL) {
    await recordAudit('seed_committed', {
      seedCommitHash: appState.seedCommit.hash,
      createdAt: appState.seedCommit.createdAt
    });
    await recordAudit('operational_mode_started', {
      totalAllocations: result.sequence.length,
      storage: 'sessionStorage'
    });
  }

  renderSummary(result);
  renderSequence(result);
  byId('mathExplanation').innerHTML = `
    ${randomizer.getExplanation()}
    <h3>Experiment Signature</h3>
    <p>The SHA-256 signature is generated from the normalized inputs and the full randomization list.</p>
    <code>${escapeHtml(appState.signature)}</code>
  `;
  saveLastResult(result, appState.signature, { sessionOnly: appState.operationMode === MODE_OPERATIONAL });
  setStep(4);
  focusCurrentStepTitle();
  showStatus(
    appState.operationMode === MODE_OPERATIONAL
      ? `${t().status.generated} ${t().operational.sessionStorageNote}`
      : t().status.generated,
    'success'
  );
}

async function generateRandomization() {
  try {
    const form = readForm();
    const randomizer = getRandomizer(form.technique);
    const result = randomizer.generate(form);
    await renderResult(result, randomizer);
  } catch (error) {
    showStatus(error.message, 'error');
  }
}

function downloadFile(filename, content, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function getDownloadName(extension) {
  const name = (appState.result.parameters.studyName || 'randflow-study')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `${name || 'randflow-study'}-${appState.result.algorithmId}.${extension}`;
}

function requireResult() {
  if (!appState.result) {
    throw new Error(t().status.needResult);
  }
}

function hasConcealedAllocations() {
  return isOperationalActive() && appState.operationalSession.revealedCount < appState.result.sequence.length;
}

function requireFullListExportAllowed() {
  if (hasConcealedAllocations()) {
    throw new Error(t().operational.fullExportBlocked);
  }
}

async function exportCsv() {
  try {
    requireResult();
    requireFullListExportAllowed();
    await recordAudit('export_csv', { filename: getDownloadName('csv'), signature: appState.signature });
    downloadFile(getDownloadName('csv'), resultToCsv(appState.result, appState.signature), 'text/csv;charset=utf-8');
  } catch (error) {
    showStatus(error.message, 'error');
  }
}

async function exportJson() {
  try {
    requireResult();
    requireFullListExportAllowed();
    await recordAudit('export_json', { filename: getDownloadName('json'), signature: appState.signature });
    downloadFile(getDownloadName('json'), resultToJson({
      ...appState.result,
      experimentSignature: appState.signature,
      language: appState.language,
      auditTrail: appState.auditTrail.snapshot()
    }), 'application/json;charset=utf-8');
  } catch (error) {
    showStatus(error.message, 'error');
  }
}

async function exportRScript() {
  try {
    requireResult();
    requireFullListExportAllowed();
    await recordAudit('export_r_script', { filename: getDownloadName('R'), signature: appState.signature });
    downloadFile(getDownloadName('R'), resultToRScript(appState.result, appState.signature), 'text/plain;charset=utf-8');
  } catch (error) {
    showStatus(error.message, 'error');
  }
}

async function exportPythonScript() {
  try {
    requireResult();
    requireFullListExportAllowed();
    await recordAudit('export_python_script', { filename: getDownloadName('py'), signature: appState.signature });
    downloadFile(getDownloadName('py'), resultToPythonScript(appState.result, appState.signature), 'text/plain;charset=utf-8');
  } catch (error) {
    showStatus(error.message, 'error');
  }
}

async function exportMethods() {
  try {
    requireResult();
    requireFullListExportAllowed();
    await recordAudit('export_methods_text', { filename: getDownloadName('methods.txt'), signature: appState.signature });
    downloadFile(
      getDownloadName('methods.txt'),
      resultToMethodsText(appState.result, appState.signature, { language: appState.language }),
      'text/plain;charset=utf-8'
    );
  } catch (error) {
    showStatus(error.message, 'error');
  }
}

async function exportAuditTrail() {
  try {
    requireResult();
    const snapshot = appState.auditTrail.snapshot();
    await recordAudit('export_audit_trail', { eventCount: snapshot.eventCount, chainHead: snapshot.chainHead });
    downloadFile(getDownloadName('audit.txt'), auditTrailToText(appState.auditTrail.snapshot()), 'text/plain;charset=utf-8');
  } catch (error) {
    showStatus(error.message, 'error');
  }
}

async function exportIntegrityManifest() {
  try {
    requireResult();
    const snapshot = appState.auditTrail.snapshot();
    await recordAudit('export_integrity_manifest', {
      signature: appState.signature,
      chainHead: snapshot.chainHead
    });
    downloadFile(
      getDownloadName('integrity-manifest.json'),
      manifestToJson(appState.result, appState.signature, appState.auditTrail.snapshot(), {
        language: appState.language
      }),
      'application/json;charset=utf-8'
    );
  } catch (error) {
    showStatus(error.message, 'error');
  }
}

async function exportOfflineQueue() {
  const queue = loadOfflineQueue();
  const payload = {
    exportedAt: new Date().toISOString(),
    language: appState.language,
    eventCount: queue.length,
    events: queue
  };
  await recordAudit('export_offline_queue', { eventCount: queue.length });
  downloadFile('randflow-offline-queue.json', JSON.stringify(payload, null, 2), 'application/json;charset=utf-8');
  showStatus(t().localData.queueExported, 'success');
}

async function clearOfflineQueueFromUi() {
  await recordAudit('clear_offline_queue_requested', { previousEventCount: loadOfflineQueue().length });
  clearOfflineQueue();
  renderLocalDataStatus();
  showStatus(t().localData.queueCleared, 'success');
}

async function clearLocalDataFromUi() {
  clearDraft();
  clearLastResult();
  if (appState.operationalSession) {
    appState.operationalSession = closeOperationalSession(appState.operationalSession);
    await recordAudit('operational_session_closed', {
      revealedCount: appState.operationalSession.revealedCount,
      total: appState.operationalSession.total
    });
  }
  await recordAudit('clear_local_data', { cleared: ['draft', 'lastResult'] });
  appState.result = null;
  appState.signature = null;
  appState.seedCommit = null;
  renderLocalDataStatus();
  renderOperationalPanel();
  showStatus(t().localData.localDataCleared, 'success');
}

async function copySignature() {
  try {
    requireResult();

    if (navigator.clipboard) {
      await navigator.clipboard.writeText(appState.signature);
      await recordAudit('copy_experiment_signature', { signature: appState.signature });
      showStatus(t().status.signatureCopied, 'success');
    } else {
      showStatus(`Experiment signature: ${appState.signature}`, 'info');
    }
  } catch (error) {
    showStatus(error.message, 'error');
  }
}

async function openReport() {
  try {
    requireResult();
    requireFullListExportAllowed();
    await recordAudit('open_pdf_report', { signature: appState.signature });
    const reportWindow = window.open('', '_blank');
    reportWindow.document.write(buildReportHtml(appState.result, appState.signature, appState.auditTrail.snapshot(), {
      language: appState.language
    }));
    reportWindow.document.close();
  } catch (error) {
    showStatus(error.message, 'error');
  }
}

async function revealNextAllocationFromUi() {
  try {
    requireResult();
    const { session, allocation } = revealNextAllocation(appState.operationalSession, appState.result);

    if (!allocation) {
      showStatus(t().operational.allRevealed, 'info');
      return;
    }

    appState.operationalSession = session;
    await recordAudit('allocation_revealed', {
      patientId: allocation.patientId,
      sequenceOrder: allocation.sequenceOrder,
      revealedCount: session.revealedCount,
      remaining: appState.result.sequence.length - session.revealedCount
    });
    renderSequence(appState.result);
    showStatus(t().operational.allocationRevealed, 'success');
  } catch (error) {
    showStatus(error.message, 'error');
  }
}

async function copySeedCommit() {
  try {
    requireResult();

    if (!appState.seedCommit) {
      throw new Error(t().operational.verificationError);
    }

    const content = JSON.stringify(appState.seedCommit, null, 2);
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(content);
    }
    await recordAudit('seed_commit_copied', { seedCommitHash: appState.seedCommit.hash });
    showStatus(t().operational.commitCopied, 'success');
  } catch (error) {
    showStatus(error.message, 'error');
  }
}

async function exportOperationalAllocation() {
  try {
    requireResult();

    if (!isOperationalActive()) {
      throw new Error(t().operational.fullExportBlocked);
    }

    const operationalResult = buildOperationalExport(appState.result, appState.signature, appState.operationalSession);
    const filename = getDownloadName('operational.csv');
    await recordAudit('allocation_exported', {
      filename,
      revealedCount: appState.operationalSession.revealedCount,
      concealedCount: appState.result.sequence.length - appState.operationalSession.revealedCount
    });
    downloadFile(filename, resultToOperationalCsv(operationalResult, appState.signature), 'text/csv;charset=utf-8');
  } catch (error) {
    showStatus(error.message, 'error');
  }
}

async function exportIntegrityBundle() {
  try {
    requireResult();
    const operationalResult = isOperationalActive()
      ? buildOperationalExport(appState.result, appState.signature, appState.operationalSession)
      : null;
    const csvName = getDownloadName(isOperationalActive() ? 'operational.csv' : 'csv');
    const jsonName = getDownloadName(isOperationalActive() ? 'operational.json' : 'json');
    const csvContent = isOperationalActive()
      ? resultToOperationalCsv(operationalResult, appState.signature)
      : resultToCsv(appState.result, appState.signature);
    const jsonContent = resultToJson(isOperationalActive() ? operationalResult : {
      ...appState.result,
      experimentSignature: appState.signature,
      language: appState.language,
      auditTrail: appState.auditTrail.snapshot()
    });
    const manifest = await buildExportIntegrityManifest([
      { name: csvName, content: csvContent },
      { name: jsonName, content: jsonContent }
    ], {
      seed: appState.result.seed,
      signature: appState.signature
    });
    const csvHash = await hashArtifact(csvName, csvContent, appState.result.seed);

    await recordAudit('export_integrity_manifest_bundle', {
      artifactCount: manifest.artifacts.length,
      signature: appState.signature
    });
    downloadFile(getDownloadName('export-integrity.json'), JSON.stringify(manifest, null, 2), 'application/json;charset=utf-8');
    downloadFile(`${csvName}.sha256`, sha256Sidecar(csvName, csvHash.sha256), 'text/plain;charset=utf-8');
  } catch (error) {
    showStatus(error.message, 'error');
  }
}

function fillVerificationWithCurrentResult() {
  try {
    requireResult();
    byId('verificationInput').value = JSON.stringify({
      technique: appState.result.algorithmId,
      seed: appState.result.seed,
      parameters: appState.result.parameters,
      expectedSignature: appState.signature
    }, null, 2);
  } catch (error) {
    showStatus(error.message, 'error');
  }
}

async function runIndependentVerification() {
  try {
    const result = await verifyRandomization(byId('verificationInput').value);
    const isPass = result.status === 'PASS';
    byId('verificationOutput').innerHTML = `
      <strong>${escapeHtml(isPass ? t().operational.verificationPass : t().operational.verificationFail)}</strong>
      <p>${escapeHtml(result.message)}</p>
      <code>${escapeHtml(result.actualSignature)}</code>
    `;
    await recordAudit('independent_verification_run', {
      status: result.status,
      expectedSignature: result.expectedSignature,
      actualSignature: result.actualSignature
    });
  } catch (error) {
    byId('verificationOutput').textContent = `${t().operational.verificationError} ${error.message}`;
  }
}

function copySeed() {
  const seed = byId('seed').value;

  if (navigator.clipboard) {
    navigator.clipboard.writeText(seed);
    showStatus(t().status.seedCopied, 'success');
  } else {
    showStatus(`Seed: ${seed}`, 'info');
  }
}

function getSelectedTechnique() {
  return document.querySelector('input[name="technique"]:checked').value;
}

function downloadCsvTemplate() {
  try {
    const technique = getSelectedTechnique();
    const csv = templateToCsv(technique);
    downloadFile(`randflow-${technique}-template.csv`, csv, 'text/csv;charset=utf-8');
  } catch (error) {
    showStatus(error.message, 'error');
  }
}

function getSelectedStratificationColumns() {
  const checkedColumns = Array.from(document.querySelectorAll('[data-stratification-column]:checked'))
    .map((input) => input.value);

  if (checkedColumns.length > 0) {
    return checkedColumns.join(',');
  }

  return byId('stratificationColumns').value;
}

function getCsvDraftInputValue() {
  if (!appState.csvDraft) {
    throw new Error(t().status.needCsv);
  }

  return rowsToTechniqueInput(appState.csvDraft.technique, appState.csvDraft.rows, {
    stratificationColumns: getSelectedStratificationColumns()
  });
}

function summarizeGroupedInput(inputValue) {
  const groups = String(inputValue || '')
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean);
  const total = groups
    .map((token) => {
      const match = token.match(/:(\d+)\s*$/);
      return match ? Number(match[1]) : 0;
    })
    .reduce((sum, size) => sum + size, 0);

  return { groups, total };
}

function renderCsvColumnPicker() {
  const container = byId('csvColumnPicker');

  if (!appState.csvDraft || !appState.csvDraft.isParticipantStratified) {
    container.hidden = true;
    container.innerHTML = '';
    return;
  }

  const columns = inferStratificationColumns(appState.csvDraft.rows);
  const selected = new Set(getSelectedStratificationColumns()
    .split(',')
    .map((column) => column.trim().toLowerCase())
    .filter(Boolean));

  container.hidden = false;
  container.innerHTML = `
    <p class="microcopy">Colunas de estratificação</p>
    <div class="csv-column-options">
      ${columns.map((column) => `
        <label class="csv-column-option">
          <input
            type="checkbox"
            value="${escapeHtml(column)}"
            data-stratification-column
            ${selected.has(column) ? 'checked' : ''}
          >
          <span>${escapeHtml(column)}</span>
        </label>
      `).join('')}
    </div>
  `;

  container.querySelectorAll('[data-stratification-column]').forEach((input) => {
    input.addEventListener('change', () => {
      const checked = Array.from(container.querySelectorAll('[data-stratification-column]:checked'))
        .map((checkbox) => checkbox.value);
      byId('stratificationColumns').value = checked.join(',');
      renderCsvPreviewSummary();
    });
  });
}

function renderCsvPreviewTable() {
  const table = byId('csvPreviewTable');
  const headers = appState.csvDraft.headers;
  const rows = appState.csvDraft.rows.slice(0, 6);

  table.innerHTML = `
    <thead>
      <tr>${headers.map((header) => `<th>${escapeHtml(header)}</th>`).join('')}</tr>
    </thead>
    <tbody>
      ${rows.map((row) => `
        <tr>${headers.map((header) => `<td>${escapeHtml(row[header])}</td>`).join('')}</tr>
      `).join('')}
    </tbody>
  `;
}

function renderCsvPreviewSummary() {
  const summary = byId('csvPreviewSummary');

  try {
    const inputValue = getCsvDraftInputValue();
    const grouped = summarizeGroupedInput(inputValue);
    const columnText = appState.csvDraft.isParticipantStratified
      ? ` ${t().parameters.strataColumns}: ${getSelectedStratificationColumns() || '-'}.`
      : '';

    summary.textContent = `${appState.csvDraft.filename}: ${appState.csvDraft.rows.length} rows, ${grouped.groups.length} groups, N=${grouped.total}.${columnText}`;
    summary.dataset.type = 'success';
    byId('applyCsvButton').disabled = false;
  } catch (error) {
    summary.textContent = error.message;
    summary.dataset.type = 'error';
    byId('applyCsvButton').disabled = true;
  }
}

function renderCsvPreview() {
  const preview = byId('csvPreview');

  if (!appState.csvDraft) {
    preview.hidden = true;
    byId('csvPreviewTable').innerHTML = '';
    byId('csvPreviewSummary').textContent = '';
    return;
  }

  preview.hidden = false;
  renderCsvColumnPicker();
  renderCsvPreviewTable();
  renderCsvPreviewSummary();
}

function clearCsvDraft() {
  appState.csvDraft = null;
  renderCsvPreview();
}

function syncParticipantCountFromGroupedInput(inputValue) {
  const total = String(inputValue || '')
    .split(',')
    .map((token) => {
      const match = token.match(/:(\d+)\s*$/);
      return match ? Number(match[1]) : 0;
    })
    .reduce((sum, size) => sum + size, 0);

  if (total > 0) {
    byId('nPatients').value = String(total);
  }
}

function importCsvTemplate(event) {
  const file = event.target.files[0];

  if (!file) {
    return;
  }

  const reader = new FileReader();
  reader.onload = () => {
    try {
      const technique = getSelectedTechnique();
      const targetField = getCsvTargetField(technique);

      if (!targetField) {
        throw new Error(t().status.csvUnavailable);
      }

      const rows = parseCsv(reader.result);
      appState.csvDraft = {
        filename: file.name,
        technique,
        targetField,
        rows,
        headers: Object.keys(rows[0] || {}),
        isParticipantStratified: technique === 'stratified-blocked' || technique === 'stratified-random-block'
      };
      renderCsvPreview();
      recordAudit('csv_preview_loaded', {
        filename: file.name,
        technique,
        rows: rows.length
      });
      showStatus(t().status.csvLoaded, 'info');
    } catch (error) {
      showStatus(error.message, 'error');
    } finally {
      event.target.value = '';
    }
  };
  reader.readAsText(file);
}

function applyCsvDraft() {
  try {
    const inputValue = getCsvDraftInputValue();
    const grouped = summarizeGroupedInput(inputValue);
    byId(appState.csvDraft.targetField).value = inputValue;
    syncParticipantCountFromGroupedInput(inputValue);
    recordAudit('csv_applied', {
      technique: appState.csvDraft.technique,
      targetField: appState.csvDraft.targetField,
      groups: grouped.groups.length,
      n: grouped.total
    });
    updateTechniqueParameters();
    clearCsvDraft();
    showStatus(t().status.csvApplied, 'success');
  } catch (error) {
    showStatus(error.message, 'error');
  }
}

function collectDraft() {
  return {
    studyName: byId('studyName').value,
    nPatients: byId('nPatients').value,
    nArms: byId('nArms').value,
    armLabels: Array.from(document.querySelectorAll('[data-arm-label-input]')).map((input) => input.value),
    technique: getSelectedTechnique(),
    seed: byId('seed').value,
    blockSize: byId('blockSize').value,
    blockSizes: byId('blockSizes').value,
    clusterSizes: byId('clusterSizes').value,
    strataSizes: byId('strataSizes').value,
    clusterStrata: byId('clusterStrata').value,
    clusterStrataSizes: byId('clusterStrataSizes').value,
    stratificationColumns: byId('stratificationColumns').value,
    operationMode: getSelectedOperationMode(),
    currentStep: appState.currentStep,
    language: appState.language
  };
}

function scheduleDraftSave() {
  if (appState.isRestoring) {
    return;
  }

  clearTimeout(appState.saveDraftTimer);
  appState.saveDraftTimer = setTimeout(() => {
    saveDraft(collectDraft());
  }, 160);
}

function restoreDraft() {
  const draft = loadDraft();

  if (!draft) {
    return false;
  }

  appState.isRestoring = true;
  byId('studyName').value = draft.studyName || '';
  byId('nPatients').value = draft.nPatients || '100';
  byId('nArms').value = draft.nArms || '2';
  updateArmLabelInputs();
  Array.from(document.querySelectorAll('[data-arm-label-input]')).forEach((input, index) => {
    if (draft.armLabels && draft.armLabels[index]) {
      input.value = draft.armLabels[index];
    }
  });
  if (draft.technique && document.querySelector(`input[name="technique"][value="${draft.technique}"]`)) {
    document.querySelector(`input[name="technique"][value="${draft.technique}"]`).checked = true;
  }
  if (draft.operationMode && document.querySelector(`input[name="operationMode"][value="${draft.operationMode}"]`)) {
    document.querySelector(`input[name="operationMode"][value="${draft.operationMode}"]`).checked = true;
    appState.operationMode = draft.operationMode;
  }
  [
    'seed',
    'blockSize',
    'blockSizes',
    'clusterSizes',
    'strataSizes',
    'clusterStrata',
    'clusterStrataSizes',
    'stratificationColumns'
  ].forEach((id) => {
    if (draft[id] !== undefined && byId(id)) {
      byId(id).value = draft[id];
    }
  });
  appState.isRestoring = false;
  showStatus(t().status.restoredDraft, 'success');
  return true;
}

function restoreLastResult() {
  const saved = loadLastResult();

  if (!saved || !saved.result || !saved.signature) {
    return false;
  }

  appState.result = saved.result;
  appState.signature = saved.signature;
  appState.operationMode = saved.persistence === 'session' ? MODE_OPERATIONAL : MODE_PLANNING;
  appState.operationalSession = null;
  appState.seedCommit = null;
  renderSummary(saved.result);
  renderSequence(saved.result);
  byId('mathExplanation').innerHTML = `
    <h3>Experiment Signature</h3>
    <p>${escapeHtml(t().status.restoredResult)}</p>
    <code>${escapeHtml(saved.signature)}</code>
  `;
  renderAuditStatus();
  showStatus(t().status.restoredResult, 'success');
  return true;
}

function initializeApp() {
  appState.language = normalizeLanguage(loadLanguage() || 'pt');
  byId('languageSelect').value = appState.language;
  initializeTheme();
  recordAudit('session_started', {
    version: APP_VERSION,
    userAgent: navigator.userAgent,
    offlineQueueLength: loadOfflineQueue().length
  });
  byId('seed').value = generateSeed();
  byId('languageSelect').addEventListener('change', () => {
    applyLanguage(byId('languageSelect').value, true);
    scheduleDraftSave();
  });
  byId('themeToggle').addEventListener('click', () => {
    const nextTheme = document.documentElement.dataset.theme === 'dark' ? 'light' : 'dark';
    applyTheme(nextTheme, true);
  });
  byId('newSeedButton').addEventListener('click', () => {
    byId('seed').value = generateSeed();
    renderMethodPreview(getSelectedTechnique());
    scheduleDraftSave();
  });
  byId('seed').addEventListener('input', () => {
    renderMethodPreview(getSelectedTechnique());
    scheduleDraftSave();
  });
  byId('nArms').addEventListener('change', () => {
    updateArmLabelInputs();
    updateTechniqueParameters();
    scheduleDraftSave();
  });
  byId('blockSize').addEventListener('change', () => {
    updateTechniqueParameters();
    scheduleDraftSave();
  });
  byId('copySeedButton').addEventListener('click', copySeed);
  byId('downloadTemplateButton').addEventListener('click', downloadCsvTemplate);
  byId('csvImport').addEventListener('change', importCsvTemplate);
  byId('applyCsvButton').addEventListener('click', applyCsvDraft);
  byId('clearCsvPreviewButton').addEventListener('click', clearCsvDraft);
  byId('generateButton').addEventListener('click', generateRandomization);
  byId('exportCsvButton').addEventListener('click', exportCsv);
  byId('exportJsonButton').addEventListener('click', exportJson);
  byId('exportRButton').addEventListener('click', exportRScript);
  byId('exportPythonButton').addEventListener('click', exportPythonScript);
  byId('exportMethodsButton').addEventListener('click', exportMethods);
  byId('exportAuditButton').addEventListener('click', exportAuditTrail);
  byId('exportManifestButton').addEventListener('click', exportIntegrityManifest);
  byId('exportQueueButton').addEventListener('click', exportOfflineQueue);
  byId('clearQueueButton').addEventListener('click', clearOfflineQueueFromUi);
  byId('clearLocalDataButton').addEventListener('click', clearLocalDataFromUi);
  byId('copySignatureButton').addEventListener('click', copySignature);
  byId('reportButton').addEventListener('click', openReport);
  byId('revealNextButton').addEventListener('click', revealNextAllocationFromUi);
  byId('copySeedCommitButton').addEventListener('click', copySeedCommit);
  byId('exportOperationalButton').addEventListener('click', exportOperationalAllocation);
  byId('exportIntegrityBundleButton').addEventListener('click', exportIntegrityBundle);
  byId('fillVerificationButton').addEventListener('click', fillVerificationWithCurrentResult);
  byId('runVerificationButton').addEventListener('click', runIndependentVerification);
  byId('nPatients').addEventListener('change', () => {
    updateTechniqueParameters();
    scheduleDraftSave();
  });
  document.querySelectorAll('input[name="operationMode"]').forEach((input) => {
    input.addEventListener('change', () => {
      appState.operationMode = getSelectedOperationMode();
      scheduleDraftSave();
    });
  });
  byId('resetButton').addEventListener('click', () => {
    byId('randFlowForm').reset();
    clearDraft();
    byId('seed').value = generateSeed();
    updateArmLabelInputs();
    updateTechniqueParameters();
    goToStep(1);
    showStatus('', 'info');
  });

  document.querySelectorAll('[data-next-step]').forEach((button) => {
    button.addEventListener('click', () => goToStep(Number(button.dataset.nextStep)));
  });

  document.querySelectorAll('[data-prev-step]').forEach((button) => {
    button.addEventListener('click', () => goToStep(Number(button.dataset.prevStep)));
  });

  document.querySelectorAll('input[name="technique"]').forEach((input) => {
    input.addEventListener('change', () => {
      updateTechniqueParameters();
      scheduleDraftSave();
    });
  });

  document.querySelectorAll('[data-method-filter]').forEach((button) => {
    button.addEventListener('click', () => {
      applyMethodFilter(button.dataset.methodFilter);
      updateTechniqueParameters();
    });
  });

  byId('randFlowForm').addEventListener('input', scheduleDraftSave);
  byId('randFlowForm').addEventListener('change', scheduleDraftSave);

  renderStaticText();
  updateArmLabelInputs();
  restoreDraft();
  applyMethodFilter('all');
  updateTechniqueParameters();
  if (!restoreLastResult()) {
    renderAuditStatus();
  }
  setStep(appState.result ? 4 : 1);
  exposeDevelopmentDiagnostics();
}

function exposeDevelopmentDiagnostics() {
  const debugHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);

  if (!debugHost) {
    return;
  }

  window.randFlow = Object.freeze({
    debug: true,
    version: APP_VERSION,
    randomizerIds: Object.keys(randomizers)
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeApp);
  } else {
    initializeApp();
  }
}

module.exports = {
  appState,
  generateRandomization,
  readForm
};
